import React, { useState, useEffect, createContext, useContext } from 'react';
import { getToken, verifyToken, removeToken } from './api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Detail from './pages/Detail';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const globalStyles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #fafaf8;
    --bg-2: #ffffff;
    --bg-3: #f4f3f0;
    --bg-4: #ede9e3;
    --border: #e2ddd6;
    --border-dark: #c8c2b8;
    --accent: #8B1A1A;
    --accent-light: #fdf5f5;
    --accent-dim: #b85c5c;
    --text: #1a1714;
    --text-dim: #4a4540;
    --text-muted: #8a837a;
    --text-light: #b8b2aa;
    --font-display: 'Playfair Display', Georgia, serif;
    --font-body: 'IBM Plex Sans', system-ui, sans-serif;
    --font-mono: 'IBM Plex Mono', monospace;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.06);
    --shadow-md: 0 4px 12px rgba(0,0,0,0.08);
  }

  html, body, #root {
    height: 100%;
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-body);
    font-size: 15px;
    -webkit-font-smoothing: antialiased;
  }

  ::selection { background: #f9d5d5; color: var(--accent); }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--bg-3); }
  ::-webkit-scrollbar-thumb { background: var(--border-dark); border-radius: 3px; }
  input, button { font-family: inherit; }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in { animation: fadeIn 0.35s ease forwards; }
`;

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedRef, setSelectedRef] = useState(null);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = globalStyles;
    // Google Fonts
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap';
    document.head.appendChild(style);
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const data = await verifyToken();
          setUser(data.user);
        } catch { removeToken(); }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const navigate = (page, params = {}) => {
    setCurrentPage(page);
    if (params.ref) setSelectedRef(params.ref);
    window.scrollTo(0, 0);
  };

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: '#8a837a', letterSpacing: 2 }}>Chargement...</div>
    </div>
  );

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {!user ? <Login onLogin={setUser} />
        : currentPage === 'detail' && selectedRef
          ? <Detail reference={selectedRef} onBack={() => navigate('dashboard')} />
          : <Dashboard onSelectRef={(ref) => navigate('detail', { ref })} />
      }
    </AuthContext.Provider>
  );
}
